import { Fragment, useEffect, useMemo, useState } from "react";
import * as XLSX from 'xlsx';
import {
    Autocomplete, Box, Button, Card, CircularProgress, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ExpandLess, ExpandMore, FileDownload, PostAdd } from "@mui/icons-material";
import { getData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import { panelSx } from "../projects/projectUtils";
import {
    CATEGORY_META, formatHours, monthLabel, otherLabel, rfsLabel, targetTitle, TimesheetProjectOption, TimesheetReportRow, todayIso,
} from "./timesheetUtils";
import TimesheetGizDialog from "./TimesheetGizDialog";

type GroupBy = 'project-person' | 'person-project' | 'month-project';
type Kind = 'all' | 'project' | 'other' | 'activity';

const targetLabel = (row: TimesheetReportRow) => {
    const title = targetTitle(row);
    if (row.projectId === null) return row.otherCategoryId !== null ? `${title} (Others)` : `${title} (free text)`;
    return `${title} · ${CATEGORY_META[row.category].short}${row.rfsId !== null ? ` · ${rfsLabel(row.rfsName, row.rfsCode)}` : ''}`;
};
const targetKey = (row: TimesheetReportRow) => (row.projectId !== null
    ? `t:p${row.projectId}:${row.category}${row.rfsId !== null ? `:r${row.rfsId}` : ''}`
    : row.otherCategoryId !== null ? `t:o${row.otherCategoryId}` : `t:a${row.activity}`);
const categoryColor = (row: TimesheetReportRow) => CATEGORY_META[row.category]?.color;

type Line = { key: string; label: string; hours: number; color?: string };
type Group = Line & { children: Line[] };

const Dot = ({ color }: { color?: string }) => (color
    ? <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: color, marginRight: 1, verticalAlign: 'middle' }} />
    : null);

// Groups the aggregated rows (person x project/activity x month) into two levels.
const buildGroups = (rows: TimesheetReportRow[], groupBy: GroupBy): Group[] => {
    const outer = (r: TimesheetReportRow): Line => groupBy === 'project-person' ? { key: targetKey(r), label: targetLabel(r), hours: 0, color: categoryColor(r) }
        : groupBy === 'person-project' ? { key: `u:${r.userOid}`, label: r.userName, hours: 0 }
        : { key: `m:${r.month}`, label: monthLabel(r.month), hours: 0 };
    const inner = (r: TimesheetReportRow): Line => groupBy === 'project-person' ? { key: `u:${r.userOid}`, label: r.userName, hours: 0 }
        : { key: targetKey(r), label: targetLabel(r), hours: 0, color: categoryColor(r) };

    const groups = new Map<string, Group>();
    rows.forEach((row) => {
        const o = outer(row);
        const i = inner(row);
        const group = groups.get(o.key) ?? { ...o, children: [] };
        group.hours += row.hours;
        const child = group.children.find((c) => c.key === i.key);
        if (child) child.hours += row.hours;
        else group.children.push({ ...i, hours: row.hours });
        groups.set(o.key, group);
    });
    const result = Array.from(groups.values());
    result.forEach((g) => g.children.sort((a, b) => b.hours - a.hours));
    return groupBy === 'month-project' ? result.sort((a, b) => b.key.localeCompare(a.key)) : result.sort((a, b) => b.hours - a.hours);
};

const firstOfMonth = () => `${todayIso().slice(0, 8)}01`;

function TimesheetReport() {
    const theme = useTheme();
    const notify = useNotification();
    const [from, setFrom] = useState(firstOfMonth);
    const [to, setTo] = useState(todayIso);
    const [userOid, setUserOid] = useState('');
    const [project, setProject] = useState<TimesheetProjectOption | null>(null);
    const [kind, setKind] = useState<Kind>('all');
    const [groupBy, setGroupBy] = useState<GroupBy>('project-person');
    const [rows, setRows] = useState<TimesheetReportRow[]>([]);
    const [users, setUsers] = useState<{ oid: string; name: string }[]>([]);
    const [projects, setProjects] = useState<TimesheetProjectOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [gizOpen, setGizOpen] = useState(false);

    useEffect(() => {
        getData('timesheets/projects').then((res) => setProjects(res as TimesheetProjectOption[])).catch((error) => console.error(error));
    }, []);

    useEffect(() => {
        if (!from || !to || from > to) return;
        const params = new URLSearchParams({ from, to, kind });
        if (userOid) params.set('userOid', userOid);
        if (project) params.set('projectId', String(project.id));
        setLoading(true);
        getData(`timesheets/report?${params.toString()}`)
            .then((res) => { setRows(res.rows as TimesheetReportRow[]); setUsers(res.users); })
            .catch((error) => notify(`Couldn't load the report: ${(error as Error).message}`, 'error'))
            .finally(() => setLoading(false));
    }, [from, to, userOid, project, kind, notify]);

    const groups = useMemo(() => buildGroups(rows, groupBy), [rows, groupBy]);
    const total = groups.reduce((sum, g) => sum + g.hours, 0);
    const maxGroup = Math.max(1, ...groups.map((g) => g.hours));

    const toggle = (key: string) => setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const exportExcel = () => {
        const sheet = XLSX.utils.json_to_sheet(rows.map((r) => ({
            Person: r.userName,
            'INT #': r.projectNumber ?? '',
            Project: r.projectName ?? '',
            Client: r.client ?? '',
            RfS: r.rfsName ?? '',
            'RfS code': r.rfsCode ?? '',
            Category: CATEGORY_META[r.category]?.label ?? r.category,
            Others: r.otherCategoryId !== null ? otherLabel(r.otherCategoryCode, r.otherCategoryName) : '',
            'Free-text activity': r.activity ?? '',
            Month: r.month,
            Hours: r.hours,
        })));
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Time report');
        XLSX.writeFile(book, `time_report_${from}_${to}.xlsx`);
    };

    const Bar = ({ hours }: { hours: number }) => (
        <Box sx={{ height: 6, borderRadius: 3, backgroundColor: alpha(theme.palette.text.primary, 0.06), minWidth: 80 }}>
            <Box sx={{ height: '100%', width: `${(hours / maxGroup) * 100}%`, borderRadius: 3, backgroundColor: theme.palette.primary.main }} />
        </Box>
    );

    return (
        <Card elevation={0} sx={{ ...panelSx(theme), overflow: 'hidden' }}>
            <Grid container spacing={2} sx={{ padding: 2 }} alignItems="center">
                <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
                </Grid>
                <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)}
                        error={from > to} helperText={from > to ? 'Must be after "From"' : ''} />
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Person</InputLabel>
                        <Select label="Person" value={userOid} onChange={(e) => setUserOid(e.target.value)}>
                            <MenuItem value="">Everyone</MenuItem>
                            {users.map((u) => <MenuItem key={u.oid} value={u.oid}>{u.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Autocomplete size="small" options={projects} value={project} onChange={(_, value) => setProject(value)}
                        getOptionLabel={(o) => `${o.projectNumber ?? ''} ${o.projectName ?? ''}`.trim()}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={(params) => <TextField {...params} label="Project" placeholder="All projects" />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <ToggleButtonGroup size="small" exclusive fullWidth value={kind} onChange={(_, value) => value && setKind(value)}>
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="project">Projects</ToggleButton>
                        <ToggleButton value="other">Others</ToggleButton>
                        <ToggleButton value="activity">Free text</ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>

            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ paddingX: 2, paddingY: 1.25, borderTop: 1, borderColor: 'divider' }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Group by</InputLabel>
                    <Select label="Group by" value={groupBy} onChange={(e) => { setGroupBy(e.target.value as GroupBy); setCollapsed(new Set()); }}>
                        <MenuItem value="project-person">Project, then person</MenuItem>
                        <MenuItem value="person-project">Person, then project</MenuItem>
                        <MenuItem value="month-project">Month, then project</MenuItem>
                    </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{formatHours(total, false)} h</Box> in total
                </Typography>
                {loading && <CircularProgress size={18} />}
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" variant="outlined" startIcon={<FileDownload />} disabled={!rows.length} onClick={exportExcel}>Export to Excel</Button>
                <Button size="small" variant="contained" disableElevation startIcon={<PostAdd />} onClick={() => setGizOpen(true)}>GIZ timesheet</Button>
            </Stack>

            {groups.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" sx={{ paddingY: 6, borderTop: 1, borderColor: 'divider' }}>
                    {loading ? 'Loading…' : 'No time booked in this period.'}
                </Typography>
            ) : (
                <Table size="small" sx={{ borderTop: 1, borderColor: 'divider' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: 'text.secondary' }}>
                                {groupBy === 'project-person' ? 'Project / person' : groupBy === 'person-project' ? 'Person / project' : 'Month / project'}
                            </TableCell>
                            <TableCell sx={{ width: '35%' }} />
                            <TableCell align="right" sx={{ color: 'text.secondary', width: 110 }}>Hours</TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', width: 90 }}>Share</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map((group) => {
                            const open = !collapsed.has(group.key);
                            return (
                                <Fragment key={group.key}>
                                    <TableRow hover onClick={() => toggle(group.key)} sx={{ cursor: 'pointer', backgroundColor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <IconButton size="small" sx={{ marginRight: 0.5 }} aria-label={open ? 'Collapse' : 'Expand'}>
                                                {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                            </IconButton>
                                            <Dot color={group.color} />{group.label}
                                        </TableCell>
                                        <TableCell><Bar hours={group.hours} /></TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatHours(group.hours, false)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'text.secondary' }}>{total ? `${Math.round((group.hours / total) * 100)} %` : '-'}</TableCell>
                                    </TableRow>
                                    {open && group.children.map((child) => (
                                        <TableRow key={`${group.key}-${child.key}`}>
                                            <TableCell sx={{ paddingLeft: 7 }}><Dot color={child.color} />{child.label}</TableCell>
                                            <TableCell />
                                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatHours(child.hours, false)}</TableCell>
                                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{group.hours ? `${Math.round((child.hours / group.hours) * 100)} %` : '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
            <TimesheetGizDialog open={gizOpen} onClose={() => setGizOpen(false)} initialProjectId={project?.id ?? null}
                initialUserOid={userOid || null} initialMonth={from.slice(0, 7)} />
        </Card>
    );
}

export default TimesheetReport;
